import { Link, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";

import { isApiClientError, isApiTransportError } from "../api/errors";

export type ErrorStatus = 401 | 403 | 404 | 409 | 422 | 429 | 503;

const errorContent: Record<
  ErrorStatus,
  { eyebrow: string; title: string; description: string }
> = {
  401: {
    eyebrow: "Требуется вход",
    title: "Войдите в ERP",
    description:
      "Сессия отсутствует или завершилась. После входа мы вернём вас на исходную страницу.",
  },
  403: {
    eyebrow: "Доступ ограничен",
    title: "Недостаточно прав",
    description:
      "Раздел недоступен для вашей роли и выбранного КПО. Скрытые данные не загружались.",
  },
  404: {
    eyebrow: "Страница не найдена",
    title: "Здесь пока ничего нет",
    description: "Проверьте адрес или вернитесь в доступный раздел ERP.",
  },
  409: {
    eyebrow: "Конфликт изменений",
    title: "Данные уже изменились",
    description:
      "Обновите страницу и повторите действие с актуальной версией данных.",
  },
  422: {
    eyebrow: "Проверьте данные",
    title: "Не удалось сохранить",
    description: "Исправьте отмеченные поля и отправьте форму ещё раз.",
  },
  429: {
    eyebrow: "Слишком много запросов",
    title: "Нужна небольшая пауза",
    description: "Подождите немного перед повторной попыткой.",
  },
  503: {
    eyebrow: "Сервис временно недоступен",
    title: "Не удалось связаться с ERP",
    description:
      "Мы сохранили текущий адрес. Повторите запрос через несколько секунд.",
  },
};

export function LoadingPage({ label = "Загружаем рабочее пространство" }) {
  return (
    <main className="state-page" aria-busy="true" aria-live="polite">
      <div className="state-card state-card--loading">
        <span className="spinner" aria-hidden="true" />
        <p>{label}</p>
      </div>
    </main>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <section className="empty-state">
      <span className="empty-state__mark" aria-hidden="true">
        +
      </span>
      <h2>{title}</h2>
      <p>{description}</p>
      {action === undefined ? null : (
        <button
          className="button button--secondary"
          type="button"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
    </section>
  );
}

/** Запрос не дошёл до сервера: нет сети или сервер недоступен. Это не ответ 503. */
const offlineContent = {
  eyebrow: "Нет соединения",
  title: "Не удалось связаться с сервером",
  description:
    "Проверьте подключение к интернету. Как только связь вернётся, страница загрузится сама.",
};

export function ErrorPage({
  status,
  onRetry,
  offline = false,
}: {
  status: ErrorStatus;
  onRetry?: () => void;
  offline?: boolean;
}) {
  const content = offline ? offlineContent : errorContent[status];

  return (
    <main className="state-page">
      <section className="state-card" aria-labelledby={`error-${status}`}>
        <span className="state-code" aria-hidden="true">
          {status}
        </span>
        <p className="eyebrow">{content.eyebrow}</p>
        <h1 id={`error-${status}`}>{content.title}</h1>
        <p className="state-description">{content.description}</p>
        <div className="state-actions">
          {onRetry === undefined ? null : (
            <button
              className="button button--primary"
              type="button"
              onClick={onRetry}
            >
              Повторить
            </button>
          )}
          {status === 401 ? (
            <Link className="button button--primary" to="/login">
              Войти
            </Link>
          ) : (
            <Link className="button button--secondary" to="/">
              На главную ERP
            </Link>
          )}
        </div>
      </section>
    </main>
  );
}

export function RouteError({ error }: { error: unknown }) {
  const router = useRouter();
  const offline = isApiTransportError(error);
  useEffect(() => {
    if (!offline) return;
    // Страница открылась из кеша оболочки без связи: как только сеть вернётся,
    // перечитываем маршрут сами, без нажатия «Повторить».
    const retry = () => void router.invalidate();
    window.addEventListener("online", retry);

    return () => window.removeEventListener("online", retry);
  }, [offline, router]);
  const status: ErrorStatus = isApiClientError(error)
    ? error.status === 401 ||
      error.status === 403 ||
      error.status === 404 ||
      error.status === 409 ||
      error.status === 422 ||
      error.status === 429 ||
      error.status === 503
      ? error.status
      : 503
    : 503;

  return (
    <ErrorPage
      status={status}
      offline={offline}
      onRetry={() => void router.invalidate()}
    />
  );
}
